import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import api from "../../api";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Plus, Trash2, Search } from "lucide-react";

const REF_COLORS = { invoice: "text-blue-500 border-blue-500/30", payment: "text-emerald-500 border-emerald-500/30",
  settlement: "text-emerald-500 border-emerald-500/30", causale: "text-amber-500 border-amber-500/30", manual: "text-muted-foreground" };

const errMsg = (e, t) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : t("msg.error"); };

export const GuidedEntryDialog = ({ open, onClose, accounts, causali, contacts, onSaved }) => {
  const { t, entity, fmt } = useApp();
  const [causaleId, setCausaleId] = useState("");
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), description: "", amount: "", vat_rate: "", contact_id: "", document_ref: "" });
  const [prev, setPrev] = useState(null);
  const [lines, setLines] = useState([]);
  const causale = causali.find((c) => c.id === causaleId);

  useEffect(() => {
    if (!open) { setCausaleId(""); setPrev(null); setLines([]); setForm((f) => ({ ...f, description: "", amount: "", vat_rate: "", contact_id: "", document_ref: "" })); }
  }, [open]);
  useEffect(() => { if (causale) setForm((f) => ({ ...f, vat_rate: String(causale.vat_rate ?? 0), description: f.description || causale.name })); }, [causale]);

  useEffect(() => {
    const amt = parseFloat(form.amount);
    if (!causale || !(amt > 0)) { setPrev(null); setLines([]); return; }
    const h = setTimeout(() => {
      api.post("/causali/preview", { causale_id: causale.id, amount: amt, vat_rate: form.vat_rate === "" ? null : parseFloat(form.vat_rate) })
        .then((r) => { setPrev(r.data); setLines(r.data.lines.map((l) => ({ ...l, debit: String(l.debit || ""), credit: String(l.credit || "") }))); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(h);
  }, [causale, form.amount, form.vat_rate]);

  const td = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const tc = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = lines.length > 0 && Math.abs(td - tc) < 0.01 && td > 0;

  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines([...lines, { account_code: accounts[0]?.code || "", debit: "", credit: "", amount_type: "manual" }]);

  const save = async () => {
    try {
      const contact = contacts.find((c) => c.id === form.contact_id);
      await api.post("/journal", {
        entity_id: entity, date: form.date, causale_code: causale?.code || null, contact_id: form.contact_id || null,
        document_ref: form.document_ref || null, net: prev?.net, vat: prev?.vat, total: prev?.total, vat_rate: prev?.vat_rate,
        description: [form.description, contact ? (contact.ragione_sociale || contact.name) : "", form.document_ref ? `rif. ${form.document_ref}` : ""].filter(Boolean).join(" — "),
        lines: lines.map((l) => ({ account_code: l.account_code, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })),
      });
      toast.success(t("msg.saved")); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, t)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="guided-entry-dialog">
        <DialogHeader><DialogTitle>{t("ord.newEntry")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Label>{t("ord.causale")}</Label>
              <Select value={causaleId} onValueChange={setCausaleId}>
                <SelectTrigger data-testid="entry-causale-select"><SelectValue placeholder={t("ord.pickCausale")} /></SelectTrigger>
                <SelectContent>
                  {causali.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`causale-option-${c.code}`}><span className="font-mono text-xs mr-2">{c.code}</span>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("common.date")}</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date-input" /></div>
          </div>
          {causale && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><Label>{causale.base === "net" ? t("ord.amountNet") : t("ord.amountTotal")}</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="entry-amount-input" /></div>
                <div><Label>{t("ord.vatRate")} %</Label>
                  <Input type="number" step="1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="entry-vat-input" /></div>
                <div><Label>{t("ord.docRef")}</Label>
                  <Input value={form.document_ref} onChange={(e) => setForm({ ...form, document_ref: e.target.value })} placeholder="n. fattura / ricevuta" data-testid="entry-docref-input" /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>{t("common.description")}</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="entry-description-input" /></div>
                <div><Label>{t("ord.contact")}</Label>
                  <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                    <SelectTrigger data-testid="entry-contact-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {prev && (
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid="entry-preview-totals">
                  <span>{t("ord.net")}: <b className="text-foreground font-mono">{fmt(prev.net)}</b></span>
                  <span>{t("ord.vat")}: <b className="text-foreground font-mono">{fmt(prev.vat)}</b></span>
                  <span>{t("ord.total")}: <b className="text-foreground font-mono">{fmt(prev.total)}</b></span>
                </div>
              )}
              <div className="rounded-md border border-border">
                <Table>
                  <TableHeader><TableRow><TableHead>{t("ledger.account")}</TableHead><TableHead className="w-32 text-right">{t("ledger.debit")}</TableHead><TableHead className="w-32 text-right">{t("ledger.credit")}</TableHead><TableHead className="w-10" /></TableRow></TableHeader>
                  <TableBody>
                    {lines.map((l, i) => (
                      <TableRow key={i} data-testid={`entry-line-${i}`}>
                        <TableCell>
                          <Select value={l.account_code} onValueChange={(v) => setLine(i, "account_code", v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`entry-line-account-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell><Input className="h-8 text-right font-mono text-xs" type="number" step="0.01" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} data-testid={`entry-line-debit-${i}`} /></TableCell>
                        <TableCell><Input className="h-8 text-right font-mono text-xs" type="number" step="0.01" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} data-testid={`entry-line-credit-${i}`} /></TableCell>
                        <TableCell><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-mono text-xs">
                      <TableCell><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addLine} data-testid="entry-add-line"><Plus className="h-3 w-3 mr-1" />{t("ord.addLine")}</Button></TableCell>
                      <TableCell className="text-right" data-testid="entry-total-debit">{fmt(td)}</TableCell>
                      <TableCell className="text-right" data-testid="entry-total-credit">{fmt(tc)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {!balanced && lines.length > 0 && <p className="text-xs text-destructive" data-testid="entry-unbalanced">{t("ord.unbalanced")} ({fmt(td - tc)})</p>}
              <Button className="w-full" onClick={save} disabled={!balanced} data-testid="entry-save-button">{t("common.save")}</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PrimaNotaTab = ({ accounts, causali, contacts, journal, reload }) => {
  const { t, fmt } = useApp();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rows = journal.filter((j) => !q || `${j.description} ${j.causale_code || ""} ${j.date}`.toLowerCase().includes(q.toLowerCase()));

  const remove = async (j) => {
    try { await api.delete(`/journal/${j.id}`); toast.success(t("msg.deleted")); reload(); } catch (e) { toast.error(errMsg(e, t)); }
  };

  return (
    <div className="space-y-4" data-testid="prima-nota-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder={t("ord.searchJournal")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="journal-search-input" />
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-guided-entry-button"><Plus className="h-4 w-4 mr-2" />{t("ord.newEntry")}</Button>
      </div>
      <GuidedEntryDialog open={open} onClose={() => setOpen(false)} accounts={accounts} causali={causali} contacts={contacts} onSaved={reload} />
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("common.date")}</TableHead><TableHead>{t("ord.causale")}</TableHead><TableHead>{t("common.description")}</TableHead>
                <TableHead>{t("ledger.account")}</TableHead><TableHead className="text-right">{t("ledger.debit")}</TableHead><TableHead className="text-right">{t("ledger.credit")}</TableHead><TableHead className="w-10" />
              </TableRow></TableHeader>
              <TableBody>
                {rows.slice(0, 200).flatMap((j) => j.lines.map((l, i) => (
                  <TableRow key={`${j.id}-${i}`} className={i === 0 ? "border-t-2" : ""} data-testid={`journal-row-${j.id}-${i}`}>
                    {i === 0 && (<>
                      <TableCell className="font-mono text-xs align-top" rowSpan={j.lines.length}>{j.date}</TableCell>
                      <TableCell className="align-top" rowSpan={j.lines.length}>
                        <Badge variant="outline" className={`text-[10px] font-mono ${REF_COLORS[j.ref_type] || ""}`}>{j.causale_code || t(`ord.ref.${j.ref_type}`)}</Badge>
                      </TableCell>
                      <TableCell className="align-top text-sm max-w-[260px]" rowSpan={j.lines.length}>{j.description}</TableCell>
                    </>)}
                    <TableCell className="text-xs"><span className="font-mono">{l.account_code}</span> <span className="text-muted-foreground">{l.account_name}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs">{l.debit ? fmt(l.debit) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{l.credit ? fmt(l.credit) : ""}</TableCell>
                    {i === 0 && (
                      <TableCell className="align-top" rowSpan={j.lines.length}>
                        {["manual", "causale", "settlement"].includes(j.ref_type) && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(j)} data-testid={`delete-journal-${j.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
